/*
 * Copyright (c) 2024 Ask Sage, Inc. All rights reserved.
 * This software is the confidential and proprietary information of Ask Sage, Inc.
 * You shall not disclose such confidential information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Ask Sage, Inc.
 * THIS SOFTWARE IS PROVIDED "AS IS" AND ANY EXPRESSED OR IMPLIED WARRANTIES,
 * INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND
 * FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL ASK SAGE, INC.
 * OR ITS CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,
 * EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
 * OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY
 * OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 * You may not use this software except in compliance with the License.
 * You may not modify, reverse engineer, or redistribute this software.
 */
import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiMoreHorizontal, FiEdit, FiTrash2, FiShare, FiMenu, FiCopy } from 'react-icons/fi';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, useTheme, Drawer, IconButton, Stack, Tooltip } from '@mui/material';
import { checkMode } from '../Utils/chatHelpers.js';
import { unEscapeHTML } from '../Utils/text_functions.js'

import { Navbar } from './Navbar';
import { getBotSettingItem, getUser, getWithExpiry, isBotLoggedIn, setWithExpiry } from '../AuthService.js';
import { WhatsNewDialog } from '../Popups/WhatsNew/WhatsNewDialog.js';

const SideMenu = ({ 
  clearChat, 
  chatButton,
  currentChats, 
  currentChatIndex, 
  buttonClick, 
  newChat, 
  deleteClick, 
  editClick, 
  shareClick, 
  setSideBarOpen, 
  sideBarOpen,
  hideSideBar, 
  setHideSideBar,
  copyChatToClipboard,
  mode='chat',
  chatContainsDeepAgent=() => false,
}) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const [messageKey, setMessageKey] = useState(null);
  const [whatsNewIsOpen, setWhatsNewIsOpen] = useState(false);
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(window.innerWidth < theme.breakpoints.values.md);
  const [paid, setPaid] = useState(false);
  const [cur_user, setUser] = useState(null);

  useEffect(() => {
    var user = getUser()
    setUser(user)
    setPaid(user.paid)

    if(getWithExpiry('helpMenuDismissed') === null) {
      setWhatsNewIsOpen(true)
    }
    if(isBotLoggedIn()) {
      if(getBotSettingItem('disable_whatsnew') === true) {         
        setWhatsNewIsOpen(false)
      }
    }
  }, []);

  window.addEventListener('resize', () => {
    setIsMobile(window.innerWidth < theme.breakpoints.values.md)
  });

  function closeModal() {
    // stops overwriting the local storage if the user manually opens the help menu
    if(getWithExpiry('helpMenuDismissed') === null) {
      const tenDays = 10 * 24 * 60 * 60 * 1000;
      setWithExpiry('helpMenuDismissed', true, tenDays);
    }
    setWhatsNewIsOpen(false);
  }

  function confirmDelete(e) {
    e.stopPropagation();

    setIsOpen(false);
    deleteClick(messageKey);
    setMessageKey(null);
  }
  return (
    <>
      {
        isMobile && (
          <>
          <Drawer 
            anchor='left' 
            open={sideBarOpen} 
            onClose={()=>{setSideBarOpen(false)}}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: theme.palette.appBackground,
                backgroundImage: 'none',
                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
              }
            }}
          >
            <Stack padding={1} direction={'row'} sx={{maxHeight: '100dvh'}}>
              <Navbar />
              <Stack direction={'column'} style={{width: '260px'}}>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                  <IconButton onClick={()=>{setSideBarOpen(false)}} >
                    <FiMenu />
                  </IconButton>
                  <IconButton onClick={()=>{
                    newChat();
                    setSideBarOpen(false);
                  }}>
                    <FiPlus />
                  </IconButton>
                </Stack>
                <div className="list-button" style={{ height: 'calc(100dvh - 54px)', overflowY: 'scroll'}}>
                  {chatButton.map((message, index) => (
                    checkMode(currentChats, message.key, mode) &&
                    <ChatButton key={index} message={message} setMessageKey={setMessageKey} setIsOpen={setIsOpen} currentChatIndex={currentChatIndex} buttonClick={buttonClick} editClick={editClick} shareClick={shareClick} deleteClick={deleteClick} copyChatToClipboard={copyChatToClipboard} chatContainsDeepAgent={chatContainsDeepAgent}/>
                  ))}
                </div>
              </Stack>
            </Stack>
          </Drawer>
          { whatsNewIsOpen && (
            <WhatsNewDialog cur_user={cur_user} modalIsOpen={whatsNewIsOpen} closeModal={closeModal} paid={paid}/>
          )}
        </>
      )}
      {!isMobile && <Box className="sidemenu" width={{width: hideSideBar ? '50px' : '260px'}}>
        <Stack 
          direction={ hideSideBar ? 'column' : 'row'} 
          justifyContent='space-between' 
          alignItems={hideSideBar ? 'start' : 'center'}
        >
          <IconButton size='small' onClick={()=>{ setHideSideBar(!hideSideBar) }} >
            <FiMenu />
          </IconButton>
          {hideSideBar ? 
            <IconButton size='small' onClick={()=>{ newChat() }} >
              <FiPlus />
            </IconButton> 
            :
            <Button startIcon={<FiPlus />} variant='outlined' size={'small'} onClick={newChat} color={'buttonColor'}>
              New Chat
            </Button>
          } 
        </Stack>
        { !hideSideBar && <div className="list-button">
          {
          chatButton.map((message, index) => (
            checkMode(currentChats, message.key, mode) &&
            <ChatButton key={index} message={message} setMessageKey={setMessageKey} setIsOpen={setIsOpen} currentChatIndex={currentChatIndex} buttonClick={buttonClick} editClick={editClick} shareClick={shareClick} deleteClick={deleteClick} copyChatToClipboard={copyChatToClipboard} chatContainsDeepAgent={chatContainsDeepAgent}/>
          ))}
        </div>}
      </Box>}
      <Dialog
        open={modalIsOpen}
        onClose={()=>{setIsOpen(false)}}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Chat</DialogTitle>
        <DialogContent>
          <p>Are you sure you want to delete this chat history?</p>
        </DialogContent>
        <DialogActions>
          <Button color={'error'} variant='contained' onClick={confirmDelete}>Delete</Button>
          <Button onClick={()=>{setIsOpen(false)}}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const ChatButton = ({ message, setIsOpen, buttonClick, currentChatIndex, editClick, setMessageKey, deleteClick, shareClick, copyChatToClipboard, chatContainsDeepAgent }) => {

  const theme = useTheme();

  const [isBlockMenuVisible, setIsBlockMenuVisible] = useState(false);
  const [messageContainsDeepAgent, setMessageContainsDeepAgent] = useState(false);

  function isDescendant(parent, child) {
    let node = child.parentNode;
    while (node !== null) {
      if (node === parent) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  }

  const blockRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        blockRef.current &&
        !isDescendant(buttonRef.current, event.target)
      ) {
        setIsBlockMenuVisible(false);
      }
    }

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  function toggleBlockMenuVisibility() {
    setIsBlockMenuVisible(!isBlockMenuVisible);
    if (chatContainsDeepAgent){
      setMessageContainsDeepAgent(chatContainsDeepAgent(message.key))
    }
  }

  return (
    <Tooltip title={unEscapeHTML(message.content)} placement="right" enterDelay={1000}>
      <Box 
        className={"side-menu-button " + (currentChatIndex === message.key ? 'selected' : 'notselected')} 
        data-key={message.key} 
        onClick={buttonClick} 
        sx={{
          backgroundColor: currentChatIndex === message.key ? theme.palette.sidebar.item.selected.backgroundColor : theme.palette.sidebar.item.backgroundColor,
          color: currentChatIndex === message.key ? theme.palette.sidebar.item.selected.color : theme.palette.textColorSecondary,
          '&:hover': {
            backgroundColor: currentChatIndex === message.key ? theme.palette.sidebar.item.selected.backgroundColor : theme.palette.sidebar.item.hover.backgroundColor,
          }
        }}
      >
        <div className="side-menu-button-content" data-key={message.key}>{unEscapeHTML(message.content)}</div>
        <div className='sidecar-button-group'>
          <div className='side-menu-wrapper'>
            <button 
              ref={buttonRef} 
              className="buttonNoBorder" 
              style={{color: currentChatIndex === message.key ? theme.palette.sidebar.item.selected.color : theme.palette.textColorSecondary}}
              onClick={toggleBlockMenuVisibility}
            >
              <FiMoreHorizontal />
            </button>
            {isBlockMenuVisible && (
              <div className="sidemenu-selection-block" ref={blockRef}>
                {!messageContainsDeepAgent && <button variant="light" data-key={message.key} onClick={shareClick}>Share <FiShare data-key={message.key} /></button>}
                <button variant="light" data-key={message.key} onClick={editClick}>Edit <FiEdit data-key={message.key} /></button>
                <button variant="light" data-key={message.key} onClick={(e)=>{
                  e.stopPropagation()
                  setMessageKey(message.key)
                  setIsOpen(true)
                  setIsBlockMenuVisible(false)
                }}>Delete <FiTrash2 data-key={message.key} /></button>
                <button variant="light" data-key={message.key} onClick={()=>{ copyChatToClipboard(message.key) }}>Copy Chat <FiCopy data-key={message.key}/></button>
              </div>
            )}
          </div>
        </div>
      </Box>
    </Tooltip>
  );
}
export default SideMenu